import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { initGA, trackPageView } from './utils/analytics';
import Header from './components/Header';
import Footer from './components/Footer';
import SnowEffect from './components/SnowEffect';
import SnowToggle from './components/SnowToggle';
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Login from './pages/Login';
import Register from './pages/Register';
import Checkout from './pages/Checkout';
import RepairService from './pages/RepairService';
import CustomStudio from './pages/CustomStudio';
import InitialSetup from './pages/InitialSetup';
import Privacy from './pages/Privacy';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProducts from './pages/admin/AdminProducts';
import ProductForm from './pages/admin/ProductForm';
import AdminCategories from './pages/admin/AdminCategories';
import AdminOrders from './pages/admin/AdminOrders';
import AdminRepairs from './pages/admin/AdminRepairs';
import AdminUsers from './pages/admin/AdminUsers';

// Component to track page views on route changes
function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    const pageTitles = {
      '/': 'Home',
      '/products': 'Products',
      '/cart': 'Shopping Cart',
      '/login': 'Login',
      '/register': 'Register',
      '/checkout': 'Checkout',
      '/services/repair': 'Repair Service',
      '/custom-studio': 'Custom Studio',
      '/setup': 'Initial Setup',
      '/privacy': 'Privacy Policy',
      '/admin': 'Admin Dashboard',
      '/admin/products': 'Manage Products',
      '/admin/categories': 'Manage Categories',
      '/admin/orders': 'Manage Orders',
      '/admin/repairs': 'Manage Repairs',
      '/admin/users': 'Manage Users',
    };

    const title = pageTitles[location.pathname] || 'Page';
    trackPageView(location.pathname, title);
  }, [location]);

  return null;
}

function App() {
  const [isSnowing, setIsSnowing] = useState(() => {
    // Load snow preference from localStorage
    const savedPreference = localStorage.getItem('snowEffect');
    return savedPreference ? JSON.parse(savedPreference) : true;
  });

  useEffect(() => {
    // Save snow preference to localStorage
    localStorage.setItem('snowEffect', JSON.stringify(isSnowing));
  }, [isSnowing]);

  useEffect(() => {
    // Initialize Google Analytics
    initGA();
  }, []);

  const toggleSnow = () => {
    setIsSnowing(!isSnowing);
  };

  return (
    <Router>
      <AuthProvider>
        <CartProvider>
          <div className="App">
            {isSnowing && <SnowEffect />}
            <AnalyticsTracker />
            <Header />
            <main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/products" element={<Products />} />
                <Route path="/product/:id" element={<ProductDetail />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/services/repair" element={<RepairService />} />
                <Route path="/custom-studio" element={<CustomStudio />} />
                <Route path="/setup" element={<InitialSetup />} />
                <Route path="/privacy" element={<Privacy />} />
                
                {/* Admin Routes */}
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/products" element={<AdminProducts />} />
                <Route path="/admin/products/new" element={<ProductForm />} />
                <Route path="/admin/products/edit/:id" element={<ProductForm />} />
                <Route path="/admin/categories" element={<AdminCategories />} />
                <Route path="/admin/orders" element={<AdminOrders />} />
                <Route path="/admin/repairs" element={<AdminRepairs />} />
                <Route path="/admin/users" element={<AdminUsers />} />
              </Routes>
            </main>
            <Footer />
            <SnowToggle isSnowing={isSnowing} toggleSnow={toggleSnow} />
          </div>
        </CartProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
